/**
 * GenerateRenders.tsx
 *
 * 5-step renders wizard.
 *
 * Steps: City → Theme → Venue → Details → Image Builder
 *
 * The Image Builder step replaces the old Preview + Generate flow with an
 * interactive per-render image generation experience (similar to the
 * ImageGenerationStep in the presentation wizard). Users can edit prompts,
 * add reference images, and generate/regenerate each render individually.
 *
 * The DashboardLayout header receives a `LinearTimeline` via `setHeaderTitle`
 * (same pattern as GeneratePresentation / WizardTimeline). This requires the
 * route to be listed in `FULL_BLEED_PATTERNS` in `constants/routes.ts` so
 * the page gets the full-bleed content area.
 */

import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { LinearTimeline } from '@/components/wizard';
import { SelectCity } from '@/pages/Dashboard/GeneratePresentation/steps';
import { SelectThemeStep } from '@/components/themes';
import { SelectVenueStep, RenderDetailsStep, RenderImageStep } from './steps';
import type { RenderWizardFormData, RenderWizardStep } from '@/types/wizard';
import styles from './GenerateRenders.module.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const INITIAL_FORM: RenderWizardFormData = {
  theme_slugs: [],
  event_name: '',
  num_renders: 4,
  prompt: '',
  provider_id: null,
  city: '',
  logo_url: '',
  reference_images: [],
  approved_brief: '',
};

const INDICATOR_STEPS: RenderWizardStep[] = ['city', 'theme', 'venue', 'details', 'images'];
const STEP_LABELS = ['City', 'Theme', 'Venue', 'Details', 'Build'];

// ─── Layout context type ──────────────────────────────────────────────────────

type LayoutContext = {
  setHeaderSlot: (node: React.ReactNode) => void;
  setHeaderTitle: (node: React.ReactNode) => void;
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * GenerateRenders — 5-step wizard for AI-powered venue render generation.
 *
 * State is managed with local `useState` (no Zustand store for wizard flow).
 */
export const GenerateRenders = () => {
  const { setHeaderTitle } = useOutletContext<LayoutContext>();
  const [step, setStep] = useState<RenderWizardStep>('city');
  const [formData, setFormData] = useState<RenderWizardFormData>(INITIAL_FORM);

  const currentStepIndex = INDICATOR_STEPS.indexOf(step);

  // ── Form helper ───────────────────────────────────────────────────────────

  const updateForm = <K extends keyof RenderWizardFormData>(
    key: K,
    value: RenderWizardFormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // ── Inject LinearTimeline into DashboardLayout header ─────────────────────

  useEffect(() => {
    setHeaderTitle(
      <LinearTimeline
        steps={STEP_LABELS}
        currentStep={currentStepIndex >= 0 ? currentStepIndex : STEP_LABELS.length - 1}
        onStepClick={(stepIdx) => {
          if (stepIdx < currentStepIndex) {
            setStep(INDICATOR_STEPS[stepIdx]);
          }
        }}
      />,
    );
    return () => setHeaderTitle(null);
  }, [currentStepIndex, setHeaderTitle]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={styles.wizard}>
      <div className={styles.stepContent}>
        {step === 'city' && (
          <SelectCity
            selected={formData.city}
            onSelect={(city) => {
              updateForm('city', city);
              setStep('theme');
            }}
          />
        )}

        {step === 'theme' && (
          <SelectThemeStep
            selected={formData.theme_slugs}
            onConfirm={(slugs) => {
              updateForm('theme_slugs', slugs);
              setStep('venue');
            }}
            onBack={() => setStep('city')}
            title="Select Event Themes"
            subtitle="Choose one or more themes for your venue renders"
          />
        )}

        {step === 'venue' && (
          <SelectVenueStep
            selectedId={formData.provider_id}
            city={formData.city}
            onCityChange={(c) => updateForm('city', c)}
            onSelect={(id) => {
              updateForm('provider_id', id);
              setStep('details');
            }}
            onSkip={() => setStep('details')}
            onBack={() => setStep('theme')}
          />
        )}

        {step === 'details' && (
          <RenderDetailsStep
            formData={formData}
            updateForm={updateForm}
            onNext={() => setStep('images')}
            onBack={() => setStep('venue')}
          />
        )}

        {step === 'images' && (
          <RenderImageStep
            formData={formData}
            onBack={() => setStep('details')}
          />
        )}
      </div>
    </div>
  );
};
